<template>
  <proa-modal
    :modal="modal"
    @close="onClose"
    noDefaultBodyPadding
    noCloseOnBackdrop
    fluid
  >
    <template v-slot:headerTitle>
      <proa-heading sub class="text-secondary">
        <span class="mr-3">{{ $t('common.modals.selectReservationAtModal.title') }}</span>
        <span>{{ videoRoomMaster.roomName }}</span>
      </proa-heading>
    </template>
    <div class="reservation-content">
      <div class="reservation-assets">
        <div @click="onClickPrev" :class="['prev-week', { 'disabled': requesting }]"/>
        <div @click="onClickNext" :class="['next-week', { 'disabled': requesting }]"/>
        <div @click="onClickToday" :class="['today', { 'disabled': requesting }]">
          {{ $t('common.today') }}
        </div>
      </div>
      <div class="reservation-calendar">
        <div v-if="requesting" class="loading-wrapper">
          <proa-loading class="loading"/>
        </div>
        <div v-else class="week-table" ref="weekTable">
          <table>
            <thead>
              <tr>
                <th></th>
                <th v-for="(day, i) in week" :key="i" :class="{ 'sat': i === 5, 'sun': i === 6 }">
                  <span>{{ day | moment('MM月DD日') }}</span>
                  <span class="ml-2">{{ day | moment('ddd') }}</span>
                </th>
              </tr>
            </thead>
            <tbody :class="{ 'disabled': endAtSettingModalCellId !== null }">
              <tr v-for="(harfHour, hourIndex) in harfHours" :key="hourIndex" :class="['hour-line', { 'harf': hourIndex % 2 != 0 }]">
                <th ref="harfHours">
                  {{ hourIndex % 2 == 0 ? harfHour : '' }}
                </th>
                <td
                  v-for="(day, weekIndex) in week"
                  :key="weekIndex"
                  :ref="`cell-${weekIndex}-${hourIndex}`"
                >
                  <div
                    v-if="cells[`${weekIndex}-${hourIndex}`].state === 'disabled'"
                    class="cell-inner disabled"
                  />
                  <div
                    v-else-if="cells[`${weekIndex}-${hourIndex}`].state === 'selected'"
                    @click.stop="onClickSelectedStartAtCell(`${hourIndex}-${weekIndex}`)"
                    class="cell-inner selected"
                  >
                    <span v-if="selectedHourIndex == hourIndex">
                      {{ innerStartAt | moment('HH:mm') }}〜{{ innerEndAt | moment('HH:mm') }}
                    </span>
                  </div>
                  <div
                    v-else-if="cells[`${weekIndex}-${hourIndex}`].state === 'reserved'"
                    class="cell-inner reserved"
                  >
                    <span v-if="reservedRooms.has(`${weekIndex}-${hourIndex}`)">
                      {{ getMeetingTitleByVideoRoomId(reservedRooms.get(`${weekIndex}-${hourIndex}`)) }}
                    </span>
                  </div>
                  <div
                    v-else
                    @click.stop="onClickStartAtCell({ day: day, hour: harfHour, cellId: `${hourIndex}-${weekIndex}` })"
                    :class="['cell-inner', { 'active': endAtSettingModalCellId == `${hourIndex}-${weekIndex}` }]"
                  />
                  <div
                    v-if="endAtSettingModalCellId == `${hourIndex}-${weekIndex}`"
                    @click.stop="() => {}"
                    v-click-outside="closeEndAtSettingModal"
                    class="end-at-setting-modal"
                    :ref="`endAtSettingModal-${hourIndex}-${weekIndex}`"
                  >
                    <div class="end-at-setting-modal-inner">
                      <span class="close-button" @click="closeEndAtSettingModal">
                        <img :src="require('@/assets/images/icons/cross_sm.png')"/>
                      </span>
                      <div class="start-at-caption mb-2">{{ $t('models.videoRoom.attributes.startAt') }}</div>
                      <div class="start-at mb-3">{{ tmpInnerStartAt | moment('MM月DD日 HH:mm') }}</div>
                      <div class="meeting-time-caption mb-2">{{ $t('models.videoRoom.meetingTime') }}</div>
                      <div class="mb-3">
                        <proa-select
                          v-model="meetingTime"
                          :options="meetingTimeOptions"
                          class="w-75"
                          dark
                        />
                      </div>
                      <div v-if="invalidMeetingTime" class="mb-3 text-error">{{ $t('common.modals.selectReservationAtModal.invalidMeetingTime') }}</div>
                      <proa-button
                        @click.native="onConfirmEndAtSetting"
                        :disabled="invalidMeetingTime"
                        class="w-50"
                      >
                        {{ $t('common.ok') }}
                      </proa-button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <template v-slot:footer>
      <proa-button @click.native="onClose" class="default-button-width mr-2">
        {{ $t('common.cancel') }}
      </proa-button>
      <proa-button
        @click.native="onConfirm"
        :disabled="!innerStartAt || !innerEndAt"
        class="default-button-width"
      >
        {{ $t('common.ok') }}
      </proa-button>
    </template>
  </proa-modal>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import ClickOutside from 'vue-click-outside'
import VideoRoom from '@/store/models/VideoRoom'

export default {
  name: 'SelectReservationAtByRoomModal',
  directives: {
    ClickOutside
  },
  props: {
    modal: {
      type: Boolean,
      default: false
    },
    videoRoomMaster: {
      type: Object,
      default: () => {}
    },
    startAt: [Date, String],
    endAt: [Date, String],
    reservedRoomId: String
  },
  computed: {
    getMeetingTitleByVideoRoomId: function() {
      return function(id) {
        return VideoRoom.find(id).meetingTitle
      }
    },
    harfHours: function() {
      let result = []
      for(let hour = 0; hour < 24; hour++) {
        result.push(moment({ hour }).format('HH:mm'))
        result.push(moment({ hour, minute: 30 }).format('HH:mm'))
      }
      return result
    },
    meetingTimeOptions: function() {
      return [...Array(12)].map((_, i) => {
        const time = (i + 1) * 0.5
        return {
          label: `${time}時間`,
          value: time
        }
      })
    }
  },
  data() {
    return {
      cells: {},
      currentScrollCellRef: null,
      reservedRooms: new Map(),
      currentDate: null,
      innerStartAt: null,
      innerEndAt: null,
      tmpInnerStartAt: null,
      meetingTime: null,
      invalidMeetingTime: false,
      requesting: false,
      week: null,
      endAtSettingModalCellId: null,
      selectedHourIndex: null
    }
  },
  methods: {
    initSchedule: function() {
      const today = new Date()
      this.setWeek(new Date(today.getFullYear(), today.getMonth(), today.getDate()))
    },
    async setWeek(baseDate) {
      if (this.currentDate && this.currentDate.getTime() == baseDate.getTime()) return
      this.currentDate = baseDate
      const year = baseDate.getFullYear()
      const month = baseDate.getMonth()
      let date = baseDate.getDate()
      let dayNum = baseDate.getDay()

      // TODO: 日曜日の場合は土曜日として判定させる
      if (dayNum == 0) {
        date = date - 1
        dayNum = 6
      }

      this.week = [...Array(7)].map((_, i) => {
        return new Date(year, month, date - dayNum + (i + 1))
      })

      this.$refs.weekTable.scrollTop = 0
      this.$refs.weekTable.scrollLeft = 0

      const startAt = moment(this.week[0]).format('YYYY/MM/DD HH:mm')
      const endAt = moment(this.week[0]).add(7, 'd').add(6, 'h').format('YYYY/MM/DD HH:mm')
      this.requesting = true
      await this.apiGetVideoRooms({
        videoRoomMasterId: this.videoRoomMaster.id,
        params: {
          start_at: startAt,
          end_at: endAt
        }
      })
      this.setCells()
      this.requesting = false
      if (this.currentScrollCellRef) {
        this.$nextTick(() => {
          this.$refs.weekTable.scrollTop = this.$refs[this.currentScrollCellRef][0].offsetTop - 26
        })
      }
    },
    setCells: function() {
      this.currentScrollCellRef = null
      for (const [weekIndex, day] of this.week.entries()) {
        for (const [hourIndex, harfHour] of this.harfHours.entries()) {
          const startAt = new Date(day.getFullYear(), day.getMonth(), day.getDate(), harfHour.split(':')[0], harfHour.split(':')[1])
          let state
          if (startAt < new Date(moment(new Date()).add(-30, 'm').format())) {
            state = 'disabled'
          } else {
            if (this.currentScrollCellRef === null) this.currentScrollCellRef = `cell-${weekIndex}-${hourIndex}`
            const reservedRoom = VideoRoom.query().where(videoRoom => {
              return videoRoom.videoRoomMasterId == this.videoRoomMaster.id
                     && videoRoom.id != this.reservedRoomId
                     && moment(startAt).isBetween(videoRoom.startAt, videoRoom.endAt, null, '[)')
            }).first()
            if (reservedRoom) {
              state = 'reserved'
              if (!Array.from(this.reservedRooms.values()).includes(reservedRoom.id)) {
                this.reservedRooms.set(`${weekIndex}-${hourIndex}`, reservedRoom.id)
              }
            } else if (this.innerStartAt && this.innerEndAt) {
              const result = moment(startAt).isBetween(this.innerStartAt, this.innerEndAt, null, '[)')
              if (result) {
                state = 'selected'
                if (!this.selectedHourIndex) this.selectedHourIndex = hourIndex
              }
            }
          }
          this.cells[`${weekIndex}-${hourIndex}`] = {
            state: state
          }
        }
      }
    },
    onClickPrev: function() {
      const mon = this.week[0]
      this.setWeek(new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() - 1))
    },
    onClickNext: function() {
      const sun = this.week[6]
      this.setWeek(new Date(sun.getFullYear(), sun.getMonth(), sun.getDate() + 1))
    },
    onClickToday: function() {
      this.initSchedule()
    },
    onClose: function() {
      this.$emit('close')
      this.resetData()
    },
    onConfirm: function() {
      this.$emit('confirm', {
        startAt: this.innerStartAt,
        endAt: this.innerEndAt
      })
    },
    onConfirmEndAtSetting: function() {
      this.innerStartAt = this.tmpInnerStartAt
      this.innerEndAt = new Date(moment(this.tmpInnerStartAt).add(this.meetingTime, 'h').format())
      this.selectedHourIndex = null
      this.setCells()
      this.endAtSettingModalCellId = this.meetingTime = this.tmpInnerStartAt = null
    },
    async onClickStartAtCell(args) {
      const { day, hour, cellId } = args
      await (() => {
        this.endAtSettingModalCellId = cellId
        this.tmpInnerStartAt = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour.split(':')[0], hour.split(':')[1])
      })()
      this.scrollIntoTableView(cellId)
    },
    async onClickSelectedStartAtCell(cellId) {
      await (() => {
        this.endAtSettingModalCellId = cellId
        this.tmpInnerStartAt = this.innerStartAt
      })()
      this.scrollIntoTableView(cellId)
    },
    closeEndAtSettingModal: function() {
      this.endAtSettingModalCellId = this.meetingTime = this.tmpInnerStartAt = null
    },
    scrollIntoTableView: function(cellId) {
      this.$refs[`endAtSettingModal-${cellId}`][0].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      })
    },
    resetData: function() {
      this.innerStartAt = this.innerEndAt = this.currentDate
      = this.week = this.endAtSettingModalCellId = this.selectedHourIndex = null
      this.reservedRooms = new Map()
    },
    ...mapActions({
      apiGetVideoRooms: 'entities/videoRooms/index'
    })
  },
  updated() {
    if (!this.modal || this.week) return
    this.innerStartAt = this.startAt
    this.innerEndAt = this.endAt
    this.initSchedule()
  },
  watch: {
    meetingTime: function(value) {
      if (!value) return
      this.invalidMeetingTime = VideoRoom.query().where(videoRoom => {
        return videoRoom.videoRoomMasterId == this.videoRoomMaster.id
               && videoRoom.id != this.reservedRoomId
               && (
                 moment(this.tmpInnerStartAt).isBetween(videoRoom.startAt, videoRoom.endAt, null, '[)')
                 || moment(new Date(moment(this.tmpInnerStartAt).add(this.meetingTime, 'h').format())).isBetween(videoRoom.startAt, videoRoom.endAt, null, '(]')
               )
      }).exists()
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";

.reservation-content {
  height: 100%;
  display: flex;
  flex-direction: column;

  .reservation-assets {
    display: flex;
    padding: 16px 20px 32px;

    .prev-week, .next-week {
      width: 28px;
      height: 28px;
      position: relative;
      cursor: pointer;
      background-color: white;
      border: 1px solid $dark-border-color;
      border-radius: 4px;

      &:hover {
        background-color: $light-border-color;
      }

      &:after {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        margin: auto;
        width: 8px;
        height: 8px;
        border-top: 1px solid $secondary-color;
        border-right: 1px solid $secondary-color;
      }
    }

    .prev-week {
      margin-right: 10px;

      &::after {
        left: 11px;
        transform: rotate(225deg);
      }
    }

    .next-week {
      margin-right: 20px;

      &::after {
        left: 7px;
        transform: rotate(45deg);
      }
    }

    .today {
      height: 28px;
      padding: 8px 15px;
      font-size: 12px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      background-color: white;
      border: 1px solid $dark-border-color;
      border-radius: 4px;

      &:hover {
        background-color: $light-border-color;
      }
    }

    .prev-week, .next-week, .today {
      &.disabled {
        cursor: none;
        pointer-events: none;
        opacity: 0.5;
      }
    }
  }

  .reservation-calendar {
    flex-grow: 1;
    position: relative;

    .week-table {
      position: absolute;
      width: 100%;
      height: 100%;
      padding: 0 20px 20px 0;
      overflow: auto;
      display: flex;

      table {
        width: 100%;
        text-align: center;
        white-space: nowrap;

        &:after {
          content: '';
          display: block;
          height: 20px;
        }

        th:first-child {
          position: sticky;
          left: 0;
        }

        thead th {
          position: sticky;
          top: 0;
          z-index: 1;
          font-size: 14px;
          font-weight: 600;
          background-color: white;
          color: $secondary-color;
          border: solid $light-border-color;
          border-width: 0 1px;
          padding: 0 10px 5px 10px;

          &:first-child {
            border-width: 0;
            z-index: 2;
          }

          &.sat {
            color: #5096C8;
          }

          &.sun {
            color: #F03232;
          }
        }

        tbody {
          &.disabled {
            pointer-events: none;
          }

          tr {
            th, td {
              font-size: 12px;
              font-weight: 300;
              color: $secondary-color;
              border: solid $light-border-color;
              border-width: 0 1px 0 0;
            }

            th {
              position: relative;
              padding: 0 20px;
              width: 80px;
              height: 20px;
              background-color: white;
              z-index: 1;

              &:after {
                content: "";
                position: absolute;
                display: block;
                top: calc(50% - 0.5px);
                right: -1px;
                width: 10px;
              }
            }

            td {
              position: relative;
              height: 20px;
              padding: 0;
              pointer-events: none;

              .cell-inner {
                position: absolute;
                display: block;
                cursor: pointer;
                pointer-events: all;
                left: -1px;
                width: calc(100% + 1px);
                height: 100%;
                opacity: .75;

                &:not(.disabled):not(.selected):not(.reserved) {
                  &.active, &:hover {
                    background-color: $primary-light-color;
                  }
                }

                &.disabled, &.selected, &.reserved {
                  text-align: left;

                  span {
                    color: white;
                    padding-left: 6px;
                  }
                }

                &.disabled {
                  cursor: default;
                  opacity: 0.5;
                  background-color: $background-color;
                }

                &.selected {
                  background-color: $primary-color;
                }

                &.reserved {
                  cursor: default;
                  background-color: $reserved-color;
                }
              }

              .end-at-setting-modal {
                pointer-events: all;
                position: absolute;
                top: 20px;
                left: 50%;
                padding: 0 20px 20px 0;
                z-index: 1000;

                .end-at-setting-modal-inner {
                  background-color: white;
                  box-shadow: 0px 0px 4px #00000026;
                  border-radius: 4px;
                  width: 200px;
                  padding: 15px;

                  .close-button {
                    display: flex;
                    justify-content: flex-end;
                    cursor: pointer;

                    img {
                      width: 10px;
                      height: 10px;
                    }
                  }

                  .start-at-caption, .meeting-time-caption {
                    font-size: 13px;
                    color: $secondary-color;
                  }

                  .start-at-caption {
                    margin-top: -10px;
                  }

                  .start-at {
                    font-size: 13px;
                    font-weight: 600;
                    color: black;
                  }
                }
              }
            }

            td:after {
              content: "";
              display: block;
              width: 100%;
            }

            &:not(.harf) td:after, &:not(.harf) th:after {
              border-top: 1px solid $light-border-color;
            }

            &.harf td:after, &.harf th:after {
              border-top: 1px dashed $light-border-color;
            }

            &:hover .cell-inner {
              border-top: 1px solid $primary-color;
            }
          }
        }
      }
    }
  }
}

.loading-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  .loading {
    width: 80px;
    height: 80px;
  }
}
</style>
